import { useEffect, useRef, useState } from "react";
import { Eye, ChevronDown, Check, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
    { key: "employee", label: "Employee", desc: "Ops generalist" },
    { key: "lead_technician", label: "Lead Technician", desc: "Cases + assignment" },
    { key: "technician", label: "Technician", desc: "Own cases only" },
    { key: "finance", label: "Finance", desc: "Money & payroll" },
    { key: "sales", label: "Sales", desc: "Pipeline & clients" },
];

/**
 * Super-Admin-only "View as another role" dropdown — Salesforce-inspired.
 * Renders nothing for any non-superadmin user.
 *
 * Mechanics: writes the selected role to AuthContext.setPreviewRole().
 * The PreviewBanner picks that up and renders the orange exit-strip; useAuth().hasPerm()
 * begins gating UI by the previewed role's presets instead of the user's real perms.
 */
export default function ViewAsSwitcher() {
    const { isSuperadmin, previewRole, setPreviewRole } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    if (!isSuperadmin) return null;

    const current = ROLES.find((r) => r.key === previewRole);

    return (
        <div ref={ref} className="relative" data-testid="view-as-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="view-as-toggle"
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium transition border ${
                    previewRole
                        ? "bg-amber-50 border-amber-200 text-amber-800"
                        : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
            >
                <Eye size={13} />
                <span className="hidden sm:inline">{previewRole ? `Viewing as ${current?.label || previewRole}` : "View as…"}</span>
                <span className="sm:hidden">View as</span>
                <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="
                        fixed sm:absolute z-50
                        top-[64px] sm:top-auto sm:mt-2
                        inset-x-3 sm:inset-x-auto sm:end-0
                        sm:w-72
                        rounded-xl bg-white border border-slate-200 shadow-2xl p-2
                        max-h-[calc(100vh-80px)] overflow-y-auto
                    "
                    data-testid="view-as-menu"
                >
                    <div className="px-2 pt-1.5 pb-1 font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-500">
                        Preview UI as role
                    </div>
                    <ul className="mt-1 space-y-0.5">
                        {ROLES.map((r) => {
                            const active = previewRole === r.key;
                            return (
                                <li key={r.key}>
                                    <button
                                        type="button"
                                        data-testid={`view-as-${r.key}`}
                                        onClick={() => { setPreviewRole(r.key); setOpen(false); }}
                                        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition ${
                                            active ? "bg-cyan-50 text-slate-900" : "hover:bg-slate-50 text-slate-700"
                                        }`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-medium">{r.label}</div>
                                            <div className="text-[11.5px] text-slate-500 truncate">{r.desc}</div>
                                        </div>
                                        {active && <Check size={14} className="text-cyan-600" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    {previewRole && (
                        <button
                            type="button"
                            data-testid="view-as-reset"
                            onClick={() => { setPreviewRole(null); setOpen(false); }}
                            className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-[12.5px] hover:bg-slate-800"
                        >
                            <RotateCcw size={12} /> Reset to Super Admin
                        </button>
                    )}
                    <div className="mt-2 px-2 pt-2 border-t border-slate-100 text-[11px] leading-snug text-slate-500">
                        Tabs & write actions will be gated by the previewed role&apos;s default permissions.
                    </div>
                </div>
            )}
        </div>
    );
}
